import { useState } from 'react';
import { Box, Button, IconButton, Popover, Typography } from '@mui/material';
import { MoreVert, Delete, Edit, Download, Replay } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import styles from './MoreOptions.module.scss';

export type MoreOptionsProps<T> = {
  className?: string;
  item: T;

  // Handlers
  onDownload?: (item: T) => void;
  onDelete?: (item: T) => void;
  onRename?: (item: T) => void;
  onResend?: (item: T) => void;
  onEdit?: (item: T) => void;

  // Enable / disable flags
  downloadEnabled?: boolean;
  resendEnabled?: boolean;
  deleteEnabled?: boolean;
};

export function MoreOptions<T>({
  className,
  item,

  onDownload,
  onDelete,
  onRename,
  onResend,
  onEdit,

  downloadEnabled = true,
  resendEnabled = false,
  deleteEnabled = false,
}: MoreOptionsProps<T>) {
  const { t } = useTranslation('settings');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton className={[styles.moreButton, className].filter(Boolean).join(' ')} onClick={handleOpen}>
        <MoreVert color='inherit' />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.paper',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
      >
        <Box className={styles.popoverBox} onClick={handleClose}>
          {/* Download — always visible */}
          <Button fullWidth disabled={!downloadEnabled} onClick={onDownload ? () => onDownload(item) : undefined}>
            <Download sx={{ mr: 1 }} />
            <Typography variant='body5'>{t('faxMoreOptions.download')}</Typography>
          </Button>

          {/* Resend — only render when enabled */}
          {resendEnabled && onResend && (
            <Button fullWidth onClick={() => onResend(item)}>
              <Replay sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('faxMoreOptions.resend')}</Typography>
            </Button>
          )}

          {/* Rename (Received faxes only) */}
          {onRename && (
            <Button fullWidth onClick={() => onRename(item)}>
              <Edit sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('faxMoreOptions.rename')}</Typography>
            </Button>
          )}

          {/* Edit (Contacts only) */}
          {onEdit && (
            <Button fullWidth onClick={() => onEdit(item)}>
              <Edit sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('faxMoreOptions.edit')}</Typography>
            </Button>
          )}

          {/* Delete — always visible */}
          <Button fullWidth disabled={!deleteEnabled} onClick={() => onDelete?.(item)}>
            <Delete sx={{ mr: 1 }} color={deleteEnabled ? 'error' : 'disabled'} />
            <Typography variant='body5'>{t('faxMoreOptions.delete')}</Typography>
          </Button>
        </Box>
      </Popover>
    </>
  );
}
